<template>
  <div class="flex items-center gap-4 justify-center">
    <template v-if="!isPreviewShowing">
      <div
        ref="seasonSelector"
        class="season-selector-wrap flex flex-col items-center gap-2 justify-center"
      >
        <div
          class="flex items-center gap-2 justify-center season-selector"
          @click="toggleSeasonSelector"
        >
          <h2 class="header-sub-name font-rubik text-yellow">
            {{ breakpoints.xsOnly ? 'S.' : 'Season ' }} {{ season }}
          </h2>
          <ComicsComponentIcon
            name="comics_arrow_bottom_icon"
            :class="[
              'season-selector-icon',
              isSeasonSelectorOpen ? 'is-open' : '',
            ]"
          />
        </div>

        <div
          v-if="isSeasonSelectorOpen"
          class="season-dropdown bg-blur"
        >
          <p
            v-for="item in seasonOptions"
            :key="item.label"
            class="season-option font-rubik text-white"
            :class="[
              item.value === season && !item.disabled ? 'active' : '',
              item.disabled ? 'is-disabled' : ''
            ]"
            :disabled="item.disabled"
            @click.stop="handleSeasonOptionClick(item)"
          >
            {{ item.label }}
            <span v-if="item.badge" class="season-option-badge text-red">
              UPCOMING
            </span>
          </p>
        </div>
      </div>

      <div
        ref="modeSelector"
        class="season-selector-wrap flex flex-col items-center gap-2 justify-center"
      >
        <div
          class="flex items-center gap-2 justify-center season-selector"
          @click="toggleModeSelector"
        >
          <h2 class="header-sub-name font-rubik text-yellow">
            {{ currentModeLabel }}
          </h2>
          <ComicsComponentIcon
            name="comics_arrow_bottom_icon"
            :class="[
              'season-selector-icon',
              isModeSelectorOpen ? 'is-open' : '',
            ]"
          />
        </div>

        <div
          v-if="isModeSelectorOpen"
          class="season-dropdown bg-blur"
        >
          <p
            v-for="item in modeOptions"
            :key="item.label"
            class="season-option font-rubik text-white"
            :class="[
              item.value === mode && !item.disabled ? 'active' : '',
              item.disabled ? 'is-disabled' : ''
            ]"
            :disabled="item.disabled"
            @click.stop="handleModeOptionClick(item)"
          >
            {{ item.label }}
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'HeaderSettings',
  props: {
    season: {
      type: Number,
      default: 1
    },
    mode: {
      type: Number,
      default: 1
    },
    isPreviewShowing: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    isSeasonSelectorOpen: false,
    isModeSelectorOpen: false,
    seasonOptions: [
      { value: 1, label: 'Season 1', disabled: false, badge: false },
      { value: 2, label: 'Season 2', disabled: true, badge: true },
      { value: 3, label: 'Season 3', disabled: true, badge: true },
      { value: null, label: 'To be continued', disabled: true, badge: false }
    ],
    modeOptions: [
      { value: 1, label: 'Classic comic', disabled: false },
      { value: 2, label: 'Tap to Play', disabled: false },
      { value: 3, label: 'Full Video', disabled: false },
    ],
    breakpoints: useBreakpoints()
  }),
  computed: {
    currentModeLabel () {
      const found = this.modeOptions.find(item => item.value === this.mode)
      return found ? found.label : 'Mode'
    }
  },
  mounted () {
    document.addEventListener('click', this.handleOutsideClick)
    window.addEventListener('keydown', this.handleKeyDown)
  },
  beforeUnmount () {
    document.removeEventListener('click', this.handleOutsideClick)
    window.removeEventListener('keydown', this.handleKeyDown)
  },
  methods: {
    handleKeyDown (event) {
      switch (event.key) {
      case 'Escape':
        this.isSeasonSelectorOpen = false
        this.isModeSelectorOpen = false
        break
      }
    },
    toggleSeasonSelector () {
      this.isSeasonSelectorOpen = !this.isSeasonSelectorOpen
      if (this.isSeasonSelectorOpen) {
        this.isModeSelectorOpen = false
      }
    },
    handleSeasonOptionClick (item) {
      if (item.disabled) return
      if (item.value === this.season) {
        this.isSeasonSelectorOpen = false
        return
      }
      this.$emit('setSeason', item.value)
      this.isSeasonSelectorOpen = false
    },

    toggleModeSelector () {
      this.isModeSelectorOpen = !this.isModeSelectorOpen
      if (this.isModeSelectorOpen) {
        this.isSeasonSelectorOpen = false
      }
    },
    handleModeOptionClick (item) {
      if (item.disabled) return
      if (item.value === this.mode) {
        this.isModeSelectorOpen = false
        return
      }
      this.$emit('setMode', item.value)
      this.isModeSelectorOpen = false
    },

    handleOutsideClick (event) {
      if (!this.isSeasonSelectorOpen && !this.isModeSelectorOpen) return

      const seasonEl = this.$refs.seasonSelector
      const modeEl = this.$refs.modeSelector

      const clickInsideSeason = seasonEl && seasonEl.contains(event.target)
      const clickInsideMode = modeEl && modeEl.contains(event.target)

      if (!clickInsideSeason && !clickInsideMode) {
        this.isSeasonSelectorOpen = false
        this.isModeSelectorOpen = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.season-selector-wrap {
  position: relative;
}

.season-selector {
  cursor: pointer;
  user-select: none;
}

.season-selector-icon {
  transition: transform 0.2s ease;
}
.season-selector-icon.is-open {
  transform: rotate(180deg);
}

.season-dropdown {
  position: absolute;
  top: 100%;
  margin-top: 8px;
  border-radius: 32px;
  padding: 32px 38px;
  z-index: 3;
}
.season-option {
  font-size: 24px;
  font-weight: 400;
  user-select: none;
  line-height: 28px;
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 16px;
  white-space: nowrap;
  cursor: pointer;
  position: relative;

  &:last-child {
    margin-bottom: 0;
  }

  &.active:not(.is-disabled) {
    cursor: initial;
    color: $yellow-100;
  }

  &.is-disabled {
    cursor: initial;
  }

  &:hover:not(.is-disabled) {
    color: $yellow-100;
  }

  .season-option-badge {
    font-size: 14px;
    position: absolute;
    top: 2px;
    right: -30px;
    line-height: 1;
  }
}

@media only screen and (max-width: $xs) {
  .season-selector-wrap {
    position: initial;
  }
  .season-dropdown {
    left: calc(50%);
    transform: translateX(-50%);
  }
}
</style>
